import { debounce } from 'lodash-es';
import React, { useState } from 'react';

import useKpiProducts from '../../hooks/useKpiProducts';
import AutocompleteArray, { AutocompleteArrayProps } from './AutocompleteArray';
import renderDeviceCountOption from './renderDeviceCountOption';

interface KpiProductSelectorProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<AutocompleteArrayProps<T | string, Multiple, DisableClearable, FreeSolo>, 'options'> {}

function KpiProductSelector<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({ ...rest }: KpiProductSelectorProps<T, Multiple, DisableClearable, FreeSolo>) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const throttledSetKeyword = debounce(setKeyword, 500);

  const onInputChange = (event, value, reason) => {
    // Initiate a search on all reasons except when reason is 'reset'.
    // react-hook-forms will trigger that event when user chooses a value in the dropdown
    // and cause an extraneous search on the server with that chosen value.
    if (reason !== 'reset') {
      throttledSetKeyword(value);
    }
  };

  const { data, fetchStatus } = useKpiProducts(keyword);

  const options =
    data?.data?.map((item) => ({
      label: item.product_name,
      value: item.product_name,
      count: item.distinct_devices,
    })) || [];

  const jsxProps = {
    loading: fetchStatus === 'fetching',
    options,
    renderOption: renderDeviceCountOption,
    onInputChange,
    ...rest,
    open,
    onOpen: () => {
      setOpen(true);
    },
    onClose: () => {
      setOpen(false);
    },
  };

  return (
    <AutocompleteArray
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...jsxProps}
    />
  );
}

export default KpiProductSelector;
